import { ChevronsUpDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "./ui";

type Props = { value: string; options: string[]; onChange: (v: string) => void; placeholder?: string; testId?: string; hint?: (v: string) => string | undefined };

export function Combobox({ value, options, onChange, placeholder, testId, hint }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (s ? options.filter((o) => o.toLowerCase().includes(s)) : options).slice(0, 60);
  }, [q, options]);

  return (
    <div ref={ref} className="relative">
      <div className={cn("flex h-10 w-full items-center rounded-md border border-input bg-background text-sm focus-within:ring-2 focus-within:ring-ring")}>
        <input
          data-testid={testId}
          className="flex-1 bg-transparent px-3 outline-none min-w-0"
          placeholder={placeholder}
          value={open ? q : value}
          onFocus={() => { setQ(""); setOpen(true); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter" && list[0]) { e.preventDefault(); onChange(list[0]); setOpen(false); } if (e.key === "Escape") setOpen(false); }}
        />
        {value && <button type="button" data-testid={testId ? `${testId}-clear` : undefined} className="px-1 text-muted-foreground hover:text-foreground" onClick={() => onChange("")}><X className="h-3.5 w-3.5" /></button>}
        <ChevronsUpDown className="mr-2 h-4 w-4 text-muted-foreground" />
      </div>
      {open && (
        <ul data-testid={testId ? `${testId}-list` : undefined} className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-popover p-1 text-sm shadow-lg">
          {list.length === 0 && <li className="px-2 py-1.5 text-muted-foreground">Tidak ada hasil</li>}
          {list.map((o) => (
            <li key={o} data-testid={testId ? `${testId}-option` : undefined} className={cn("cursor-pointer rounded px-2 py-1.5 hover:bg-muted flex justify-between gap-2", o === value && "bg-muted font-semibold")} onMouseDown={(e) => { e.preventDefault(); onChange(o); setOpen(false); }}>
              <span>{o}</span>
              {hint && <span className="text-xs text-muted-foreground truncate">{hint(o)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
